<div data-spartan-page class="p-6 md:p-8 space-y-6" style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page Header -->
  <app-page-header
    eyebrow="Shopee · Repack"
    title="Add Parcels to Existing Carton"
    [subtitle]="'Select an existing carton to add parcels from ' + (originalShopeeOrderNo || 'Unknown') + '.'"
  />

  <!-- Pending rebind warning -->
  @if (hasPendingRebind()) {
    <div class="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/15 dark:border-amber-500/30 p-3 text-sm text-amber-800 dark:text-amber-300">
      <ng-icon hlm name="lucideAlertTriangle" class="size-4 mt-0.5 shrink-0" />
      <div>
        <p class="font-semibold mb-1">Completion Required</p>
        <p class="text-xs">You must assign the unbinded parcels to a carton. Leaving this page without completing will result in unassigned parcels.</p>
      </div>
    </div>
  }

  <!-- Feedback alerts -->
  @if (successMessage) {
    <app-inline-alert tone="success" [message]="successMessage" />
  }
  @if (errorMessage) {
    <app-inline-alert tone="danger" [message]="errorMessage" />
  }

  <!-- Original Carton Reference Card -->
  <div hlmCard>
    <div class="px-4 py-3 border-b border-border">
      <h2 class="text-sm font-semibold text-foreground">Original Carton</h2>
    </div>
    <div class="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4">
      <div>
        <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Original Order ID</p>
        <p class="font-mono text-sm text-foreground">{{ originalOrderId || 'N/A' }}</p>
      </div>
      <div>
        <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Shopee Order No</p>
        <p class="font-mono text-sm text-foreground">{{ originalShopeeOrderNo || 'N/A' }}</p>
      </div>
      <div>
        <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Service Code</p>
        <p class="text-sm text-foreground">{{ originalServiceCode || 'N/A' }}</p>
      </div>
    </div>
  </div>

  <!-- Parcels to Add Card -->
  <div hlmCard>
    <div class="px-4 py-3 border-b border-border flex items-center justify-between">
      <h2 class="text-sm font-semibold text-foreground">Parcels to Add</h2>
      <span class="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30">
        {{ parcelsToAdd.length }}
      </span>
    </div>
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
            <th class="text-left px-4 py-2.5 font-medium">#</th>
            <th class="text-left px-4 py-2.5 font-medium">Tracking Number</th>
            <th class="text-left px-4 py-2.5 font-medium">Weight</th>
            <th class="text-left px-4 py-2.5 font-medium">Dimensions (L×W×H)</th>
          </tr>
        </thead>
        <tbody>
          @for (parcel of parcelsToAdd; track parcel.trackingNumber || $index; let i = $index) {
            <tr class="h-10 border-b border-border last:border-0 even:bg-surface-raised/50">
              <td class="px-4 text-xs tabular-nums text-muted-foreground">{{ i + 1 }}</td>
              <td class="px-4 font-mono text-[12.5px] font-medium text-foreground">{{ parcel.trackingNumber || 'N/A' }}</td>
              <td class="px-4 text-muted-foreground text-sm">{{ parcel.weight || 'N/A' }} kg</td>
              <td class="px-4 text-muted-foreground text-xs">
                {{ parcel.length || '—' }} × {{ parcel.width || '—' }} × {{ parcel.height || '—' }} cm
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>

  <!-- Select Target Carton Card -->
  <div hlmCard>
    <div class="px-4 py-3 border-b border-border bg-blue-50 dark:bg-blue-500/10 rounded-t-lg">
      <h2 class="text-sm font-semibold text-blue-700 dark:text-blue-300">Select Target Carton</h2>
    </div>
    <div class="p-4">

      @if (isLoadingCartons) {
        <div class="flex flex-col items-center justify-center py-10 gap-2">
          <div class="h-6 w-6 rounded-full border-2 border-accent border-t-transparent animate-spin"></div>
          <p class="text-sm text-muted-foreground">Loading eligible cartons…</p>
        </div>
      }

      @if (!isLoadingCartons && eligibleCartons.length === 0) {
        <div class="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/15 dark:border-amber-500/30 p-3 text-sm text-amber-800 dark:text-amber-300">
          <ng-icon hlm name="lucideAlertTriangle" class="size-4 mt-0.5 shrink-0" />
          <span>No eligible cartons found with matching service code ({{ originalServiceCode }}). Please create a new carton instead.</span>
        </div>
      }

      @if (!isLoadingCartons && eligibleCartons.length > 0) {
        <form (ngSubmit)="onSubmit()">
          <div class="mb-4">
            <label for="cartonSelect" class="block text-xs font-medium text-muted-foreground mb-2">Select a carton to add parcels to:</label>
            <div class="relative max-w-lg">
              <select id="cartonSelect"
                [(ngModel)]="selectedCartonId" name="selectedCartonId" required
                class="appearance-none w-full h-9 border border-border-strong bg-surface rounded-md pl-3 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-accent outline-none">
                <option value="" disabled selected>-- Select a carton --</option>
                @for (carton of eligibleCartons; track carton.cartonNo) {
                  <option [value]="carton.cartonNo">{{ carton.summary }}</option>
                }
              </select>
              <svg class="absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            <p class="mt-1 text-xs text-muted-foreground">
              Only cartons with matching service code ({{ originalServiceCode }}) and eligible status are shown.
            </p>
          </div>

          @if (selectedCartonId) {
            <div class="rounded-lg bg-surface-raised border border-border p-3 mb-4">
              <p class="font-semibold text-sm text-foreground mb-3">Selected Carton Details</p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                <div>
                  <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Carton No</p>
                  <p class="font-mono text-sm text-foreground">{{ (eligibleCartons | find: 'cartonNo': selectedCartonId)?.cartonNo }}</p>
                </div>
                <div>
                  <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Shopee Order No</p>
                  <p class="font-mono text-sm text-foreground">{{ (eligibleCartons | find: 'cartonNo': selectedCartonId)?.shopeeOrderNo || 'N/A' }}</p>
                </div>
                <div>
                  <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Current Parcels</p>
                  <p class="text-sm text-foreground">{{ (eligibleCartons | find: 'cartonNo': selectedCartonId)?.parcelCount || 0 }}</p>
                </div>
                <div>
                  <p class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Status</p>
                  <p class="text-sm text-foreground">{{ (eligibleCartons | find: 'cartonNo': selectedCartonId)?.status }}</p>
                </div>
              </div>
            </div>
          }

          <!-- Pagination (Bootstrap preserved — functional) -->
          @if (totalPages > 1) {
            <div class="flex justify-center mt-3 mb-4">
              <nav aria-label="Carton pagination">
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" [class.disabled]="currentPage <= 1">
                    <a class="page-link" href="javascript:void(0)" (click)="changePage(currentPage - 1)">Previous</a>
                  </li>
                  @for (page of [].constructor(totalPages); track $index; let i = $index) {
                    <li class="page-item" [class.active]="i + 1 === currentPage">
                      <a class="page-link" href="javascript:void(0)" (click)="changePage(i + 1)">{{ i + 1 }}</a>
                    </li>
                  }
                  <li class="page-item" [class.disabled]="currentPage >= totalPages">
                    <a class="page-link" href="javascript:void(0)" (click)="changePage(currentPage + 1)">Next</a>
                  </li>
                </ul>
              </nav>
            </div>
          }

          <!-- Action row inside form -->
          <div class="flex items-center justify-between gap-2 pt-3 border-t border-border">
            <button type="button" hlmBtn variant="outline" size="sm" (click)="goBack()">
              <ng-icon hlm name="lucideArrowLeft" class="mr-1.5 size-3.5" />
              Back
            </button>
            <button type="submit" hlmBtn size="sm"
              [disabled]="!selectedCartonId || isLoading || parcelsToAdd.length === 0">
              @if (isLoading) {
                <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              }
              Add Parcels to Selected Carton
            </button>
          </div>
        </form>
      }
    </div>
  </div>

</div>
